import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { authService, type AuthUser } from '../services/authService';
import type { ModuleName } from '../config/modules';
import { MODULE_FLAGS } from '../config/modules';
import { canPerformAction as checkPermission } from '../utils/permissions';

interface AuthContextType {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (identifier: string, password: string) => Promise<AuthUser>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    canAccess: (moduleCode: string) => boolean;
    canPerformAction: (moduleCode: string, actionCode: string) => boolean;
    isAdmin: () => boolean;
    isPatient: () => boolean;
    patientId: () => number | null;
    userType: () => 'patient' | 'doctor' | 'staff' | 'admin' | 'unknown';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const storedUser = authService.getUser();
        if (storedUser && authService.isAuthenticated()) {
            setUser(storedUser);
            // Refresh from server to get latest profile/modules
            authService.me().then(setUser).catch(() => { /* token may have expired */ });
        }
        setIsLoading(false);
    }, []);

    const login = useCallback(async (identifier: string, password: string) => {
        const trimmed = identifier.trim();
        const credentials = trimmed.includes('@')
            ? { email: trimmed, password }
            : { username: trimmed.toLowerCase(), password };
        const response = await authService.login(credentials);
        setUser(response.user);
        return response.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authService.logout();
        } catch {
            localStorage.removeItem('user');
        }
        setUser(null);
    }, []);

    const refreshUser = useCallback(async () => {
        const updated = await authService.me();
        setUser(updated);
    }, []);

    /**
     * Check if the current user can access a module.
     * Rules:
     *  1. If ENV flag for the module is disabled → NO (deployment-level gate)
     *  2. If user has no profile (or profile has no modules) → YES (treat as superadmin)
     *  3. If user's profile has the module code with is_active=true → YES
     */
    const canAccess = useCallback((moduleCode: string): boolean => {
        // Check ENV flag first (deployment gate)
        if (moduleCode in MODULE_FLAGS) {
            if (!MODULE_FLAGS[moduleCode as ModuleName]) return false;
        }

        if (!user) return false;

        // No profile assigned → full access (superadmin)
        if (!user.profile) return true;

        // Profile with no modules defined → full access
        if (!user.profile.modules || user.profile.modules.length === 0) return true;

        // Check if the module code is in the profile's allowed modules
        return user.profile.modules.some(m => m.code === moduleCode);
    }, [user]);

    const isAdmin = useCallback((): boolean => {
        if (!user) return false;
        // D1: is_admin is the single source of truth
        if (user.is_admin) return true;
        // Legacy heuristics for backward compatibility
        if (!user.profile) return true;
        if (user.roles.some(r => r.name.toLowerCase() === 'admin')) return true;
        return user.profile.modules.some(m => m.code === 'admin');
    }, [user]);

    const canPerformAction = useCallback((moduleCode: string, actionCode: string): boolean => {
        if (!user) return false;
        return checkPermission(user, moduleCode, actionCode);
    }, [user]);

    const isPatient = useCallback((): boolean => {
        if (!user) return false;
        return user.profile?.code === 'patient' || !!user.patient_id;
    }, [user]);

    const patientId = useCallback((): number | null => {
        if (!user) return null;
        return user.patient_id ?? null;
    }, [user]);

    const userType = useCallback((): 'patient' | 'doctor' | 'staff' | 'admin' | 'unknown' => {
        if (!user) return 'unknown';
        if (user.is_admin) return 'admin';
        if (user.patient_id) return 'patient';
        if (user.profile?.code === 'patient') return 'patient';
        if (user.doctor) return 'doctor';
        return 'staff';
    }, [user]);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user && authService.isAuthenticated(),
        isLoading,
        login,
        logout,
        refreshUser,
        canAccess,
        canPerformAction,
        isAdmin,
        isPatient,
        patientId,
        userType,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
